import React, { useCallback, useEffect, useState } from 'react';
import { Plus, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  chargeMapAdminService,
  NAME_KINDS,
  type ApprovedName,
  type NameKind,
} from '../../services/chargeMapAdminService';
import { chargeMapService } from '../../../../../features/billValidation/services/chargeMapService';

const FORM_SELECT_CLASS =
  'flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const KIND_LABEL: Record<NameKind, string> = {
  distributor: 'Distributors',
  retailer: 'Retailers',
  metering: 'Metering providers',
};

/**
 * Approved names and their other spellings (Naming Conventions Audit, Oct 2026).
 *
 * Retailer and distributor matching resolves both sides to the approved name
 * first (migrations 138/139), so a site saying "Ergon Energy" still meets a
 * tariff configured as "Ergon Energy (Distribution)". A new spelling met in the
 * data is fixed by adding it here, not by renaming the data. Case and
 * punctuation are already ignored: "Ausnet" is "AusNet" without a row.
 */
export const NameAliasesTab: React.FC = () => {
  const [kind, setKind] = useState<NameKind>('distributor');
  const [rows, setRows] = useState<ApprovedName[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  // Add an other name to one approved name.
  const [aliasFor, setAliasFor] = useState<ApprovedName | null>(null);
  // Add a new approved name.
  const [newNameOpen, setNewNameOpen] = useState(false);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [removing, setRemoving] = useState<{ alias: string; systemName: string } | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await chargeMapAdminService.names.list(kind);
    if (res.success) { setRows(res.data ?? []); setError(null); }
    else setError(res.error ?? 'Failed to load names');
    setLoading(false);
  }, [kind]);

  useEffect(() => { void load(); }, [load]);

  // Screens that resolve names hold a session cache; drop it after any change.
  const changed = async () => {
    chargeMapService.clearAliasCache();
    await load();
  };

  const openAlias = (row: ApprovedName) => { setAliasFor(row); setInput(''); setFormError(null); };
  const openNewName = () => { setNewNameOpen(true); setInput(''); setFormError(null); };

  const handleSave = async () => {
    if (!input.trim()) { setFormError('Enter a name'); return; }
    setSaving(true);
    setFormError(null);
    const res = aliasFor
      ? await chargeMapAdminService.names.addAlias(kind, input.trim(), aliasFor.system_name)
      : await chargeMapAdminService.names.create(kind, input.trim());
    setSaving(false);
    if (!res.success) { setFormError(res.error ?? 'Save failed'); return; }
    setAliasFor(null);
    setNewNameOpen(false);
    await changed();
  };

  const handleRemove = async () => {
    if (!removing) return;
    setRemoveBusy(true);
    const res = await chargeMapAdminService.names.removeAlias(kind, removing.alias);
    setRemoveBusy(false);
    setRemoving(null);
    if (!res.success) { setError(res.error ?? 'Remove failed'); return; }
    await changed();
  };

  const q = filter.trim().toLowerCase();
  const visible = q
    ? rows.filter((r) => r.system_name.toLowerCase().includes(q) || r.aliases.some((a) => a.toLowerCase().includes(q)))
    : rows;
  const formOpen = !!aliasFor || newNameOpen;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground max-w-3xl">
        Matching compares approved names, so any spelling listed here meets the others: a site saying
        &ldquo;Ergon Energy&rdquo; still finds a tariff configured as &ldquo;Ergon Energy (Distribution)&rdquo;. When a new
        spelling turns up, add it here rather than renaming the data. Case, spaces and punctuation are already ignored.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <select
          aria-label="Name type"
          value={kind}
          onChange={(e) => setKind(e.target.value as NameKind)}
          className={FORM_SELECT_CLASS}
        >
          {NAME_KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
        </select>
        <Input
          placeholder="Filter by any spelling…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-xs"
        />
        <span className="text-sm text-muted-foreground">{visible.length} of {rows.length} names</span>
        <div className="flex-1" />
        <Button size="sm" onClick={openNewName}><Plus className="h-4 w-4 mr-1" />Add approved name</Button>
      </div>

      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

      {loading ? (
        <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-64">Approved name</TableHead>
                <TableHead>Other names</TableHead>
                <TableHead className="w-40" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-muted-foreground py-6">No names.</TableCell>
                </TableRow>
              )}
              {visible.map((row) => (
                <TableRow key={row.system_name}>
                  <TableCell className="font-medium align-top">{row.system_name}</TableCell>
                  <TableCell>
                    {row.aliases.length === 0 ? (
                      <span className="text-sm text-muted-foreground">None</span>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {row.aliases.map((a) => (
                          <span key={a} className="inline-flex items-center gap-1 rounded-full border bg-muted/50 px-2 py-0.5 text-xs">
                            {a}
                            <button
                              type="button"
                              className="text-muted-foreground hover:text-destructive"
                              aria-label={`Remove ${a}`}
                              onClick={() => setRemoving({ alias: a, systemName: row.system_name })}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="align-top">
                    <Button variant="outline" size="sm" onClick={() => openAlias(row)}>
                      <Plus className="h-3.5 w-3.5 mr-1" />Other name
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add an other name / an approved name */}
      <Dialog open={formOpen} onOpenChange={(v) => { if (!v && !saving) { setAliasFor(null); setNewNameOpen(false); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{aliasFor ? `Other name for “${aliasFor.system_name}”` : `New approved ${kind} name`}</DialogTitle>
            <DialogDescription>
              {aliasFor
                ? 'Takes effect on the next NEM analysis and bill run. Bills already priced are not changed until they re-run.'
                : 'The name the system should use for this one. Add its other spellings after.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="name-input">{aliasFor ? 'Spelling' : 'Approved name'}</Label>
            <Input
              id="name-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void handleSave(); }}
              autoFocus
            />
          </div>
          {formError && <Alert variant="destructive"><AlertDescription>{formError}</AlertDescription></Alert>}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAliasFor(null); setNewNameOpen(false); }} disabled={saving}>Cancel</Button>
            <Button onClick={() => void handleSave()} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove an other name */}
      <Dialog open={!!removing} onOpenChange={(v) => { if (!v && !removeBusy) setRemoving(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove “{removing?.alias}”?</DialogTitle>
            <DialogDescription>
              Data spelled &ldquo;{removing?.alias}&rdquo; will no longer match &ldquo;{removing?.systemName}&rdquo;. A site or
              contract still using that spelling loses its tariff or TOU match on its next run.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoving(null)} disabled={removeBusy}>Cancel</Button>
            <Button variant="destructive" onClick={() => void handleRemove()} disabled={removeBusy}>
              {removeBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
